import * as THREE from 'three';
import { createMatteMaterial, createGlowMaterial } from '../../../visual';
import type { ShipSkin } from '../skins/ShipSkin';
import {
  createTaperedSlab,
  type EngineMount,
  type ShipBuildResult
} from './common';

export function buildGuardCruiser(
  parent: THREE.Object3D,
  skin: ShipSkin
): ShipBuildResult {
  const strobes: THREE.Mesh[] = [];

  buildMainHull(parent, skin);
  buildNoseCannons(parent, skin);
  buildCockpit(parent, skin);
  buildWings(parent, skin);
  buildTailFin(parent, skin);
  const engines = buildEngines(parent, skin);
  buildStrobes(parent, skin, strobes);

  return { engines, strobes, collisionRadius: 2.0 };
}

function buildMainHull(parent: THREE.Object3D, skin: ShipSkin): void {
  const hullMat = createMatteMaterial({ color: skin.hull, roughness: 0.55 });
  const primaryMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.4
  });
  const primaryDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const trimMat = createMatteMaterial({ color: skin.trim, roughness: 0.5 });
  const lightStripMat = createMatteMaterial({
    color: skin.accent,
    roughness: 0.3,
    emissive: skin.accent,
    emissiveIntensity: 0.35
  });

  const body = new THREE.Mesh(createTaperedSlab(0.55, 1.0, 0.55, 3.4), hullMat);
  body.position.set(0, 0.1, 0.05);

  const topDeck = new THREE.Mesh(
    createTaperedSlab(0.42, 0.88, 0.08, 3.2),
    primaryMat
  );
  topDeck.position.set(0, 0.42, 0.05);

  const topDeckTrim = new THREE.Mesh(
    createTaperedSlab(0.44, 0.9, 0.015, 3.25),
    trimMat
  );
  topDeckTrim.position.set(0, 0.47, 0.05);

  const belly = new THREE.Mesh(
    createTaperedSlab(0.46, 0.9, 0.18, 3.3),
    primaryDark
  );
  belly.position.set(0, -0.22, 0.05);

  const rearBlock = new THREE.Mesh(
    new THREE.BoxGeometry(1.05, 0.55, 0.4),
    primaryMat
  );
  rearBlock.position.set(0, 0.12, 1.55);

  const rearBlockTrim = new THREE.Mesh(
    new THREE.BoxGeometry(1.08, 0.06, 0.42),
    trimMat
  );
  rearBlockTrim.position.set(0, 0.42, 1.55);

  const sideLightStripGeo = new THREE.BoxGeometry(0.03, 0.06, 2.6);
  const stripLeft = new THREE.Mesh(sideLightStripGeo, lightStripMat);
  stripLeft.position.set(-0.47, 0.22, 0.05);
  const stripRight = stripLeft.clone();
  stripRight.position.x = 0.47;

  const sideAccentGeo = new THREE.BoxGeometry(0.025, 0.35, 2.4);
  const accentLeft = new THREE.Mesh(sideAccentGeo, primaryDark);
  accentLeft.position.set(-0.5, 0.06, 0.05);
  const accentRight = accentLeft.clone();
  accentRight.position.x = 0.5;

  parent.add(
    body,
    topDeck,
    topDeckTrim,
    belly,
    rearBlock,
    rearBlockTrim,
    stripLeft,
    stripRight,
    accentLeft,
    accentRight
  );
}

function buildNoseCannons(parent: THREE.Object3D, skin: ShipSkin): void {
  const primaryMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.4
  });
  const primaryDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const trimMat = createMatteMaterial({ color: skin.trim, roughness: 0.55 });
  const hullMat = createMatteMaterial({ color: skin.hull, roughness: 0.55 });
  const muzzleMat = createGlowMaterial(skin.glow, 0.9, true);

  const noseTip = new THREE.Mesh(
    new THREE.ConeGeometry(0.32, 0.85, 14),
    hullMat
  );
  noseTip.rotation.x = -Math.PI / 2;
  noseTip.position.set(0, 0.1, -2.1);

  const noseCap = new THREE.Mesh(
    new THREE.SphereGeometry(0.14, 14, 10),
    primaryMat
  );
  noseCap.position.set(0, 0.1, -2.52);

  const noseCollar = new THREE.Mesh(
    new THREE.TorusGeometry(0.34, 0.05, 8, 22),
    trimMat
  );
  noseCollar.rotation.y = Math.PI / 2;
  noseCollar.position.set(0, 0.1, -1.7);

  const cannonGeo = new THREE.CylinderGeometry(0.09, 0.11, 1.1, 14);
  const mkCannon = (sign: 1 | -1): void => {
    const cannon = new THREE.Mesh(cannonGeo, primaryDark);
    cannon.rotation.x = Math.PI / 2;
    cannon.position.set(sign * 0.38, -0.05, -1.55);

    const cannonRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.1, 0.022, 6, 16),
      trimMat
    );
    cannonRing.position.set(sign * 0.38, -0.05, -1.98);

    const muzzle = new THREE.Mesh(
      new THREE.CircleGeometry(0.07, 16),
      muzzleMat
    );
    muzzle.position.set(sign * 0.38, -0.05, -2.1);

    const mount = new THREE.Mesh(
      new THREE.BoxGeometry(0.22, 0.18, 0.28),
      primaryMat
    );
    mount.position.set(sign * 0.38, -0.05, -0.95);

    parent.add(cannon, cannonRing, muzzle, mount);
  };
  mkCannon(1);
  mkCannon(-1);

  parent.add(noseTip, noseCap, noseCollar);
}

function buildCockpit(parent: THREE.Object3D, skin: ShipSkin): void {
  const canopyMat = createMatteMaterial({
    color: skin.cockpit,
    roughness: 0.18,
    emissive: skin.cockpitGlow,
    emissiveIntensity: 0.04
  });
  const frameMat = createMatteMaterial({ color: skin.trim, roughness: 0.5 });
  const primaryMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.4
  });
  const glowMat = createMatteMaterial({
    color: skin.cockpitGlow,
    roughness: 0.25,
    emissive: skin.cockpitGlow,
    emissiveIntensity: 0.1
  });

  const base = new THREE.Mesh(
    new THREE.BoxGeometry(0.68, 0.1, 1.0),
    frameMat
  );
  base.position.set(0, 0.5, -0.35);

  const canopy = new THREE.Mesh(
    new THREE.SphereGeometry(0.36, 22, 14, 0, Math.PI * 2, 0, Math.PI / 2.1),
    canopyMat
  );
  canopy.scale.set(0.85, 0.9, 1.5);
  canopy.position.set(0, 0.55, -0.35);

  const highlight = new THREE.Mesh(
    new THREE.SphereGeometry(
      0.36,
      20,
      12,
      Math.PI * 1.4,
      Math.PI * 0.5,
      Math.PI * 0.1,
      Math.PI * 0.35
    ),
    glowMat
  );
  highlight.scale.set(0.85, 0.9, 1.5);
  highlight.position.set(0, 0.55, -0.35);

  const frameRib = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.25, 1.1),
    frameMat
  );
  frameRib.position.set(0, 0.65, -0.35);

  const dashStripe = new THREE.Mesh(
    new THREE.BoxGeometry(0.6, 0.03, 0.05),
    primaryMat
  );
  dashStripe.position.set(0, 0.5, -0.85);

  parent.add(base, canopy, highlight, frameRib, dashStripe);
}

function buildWings(parent: THREE.Object3D, skin: ShipSkin): void {
  const primaryMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.45
  });
  const primaryDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const hullMat = createMatteMaterial({ color: skin.hull, roughness: 0.55 });
  const accentMat = createMatteMaterial({
    color: skin.accent,
    roughness: 0.3,
    emissive: skin.accent,
    emissiveIntensity: 0.2
  });

  const wingShape = new THREE.Shape();
  wingShape.moveTo(0, 0);
  wingShape.lineTo(1.45, -0.1);
  wingShape.lineTo(1.55, 0.15);
  wingShape.lineTo(1.3, 0.55);
  wingShape.lineTo(0.15, 0.5);
  wingShape.closePath();

  const wingGeo = new THREE.ExtrudeGeometry(wingShape, {
    depth: 0.1,
    bevelEnabled: true,
    bevelSize: 0.035,
    bevelThickness: 0.035,
    bevelSegments: 2
  });
  wingGeo.translate(0, -0.25, -0.05);
  wingGeo.rotateX(Math.PI / 2);

  const mkSide = (sign: 1 | -1): void => {
    const wing = new THREE.Mesh(wingGeo, primaryMat);
    wing.scale.x = sign;
    wing.position.set(sign * 0.42, 0.05, 0.55);

    const wingTop = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 0.03, 0.4),
      hullMat
    );
    wingTop.position.set(sign * 1.05, 0.12, 0.72);
    wingTop.rotation.y = sign * -0.04;

    const wingLight = new THREE.Mesh(
      new THREE.BoxGeometry(0.9, 0.035, 0.06),
      accentMat
    );
    wingLight.position.set(sign * 1.0, 0.14, 0.3);
    wingLight.rotation.y = sign * -0.04;

    const wingtip = new THREE.Mesh(
      new THREE.ConeGeometry(0.07, 0.4, 10),
      primaryDark
    );
    wingtip.rotation.z = (sign * Math.PI) / 2;
    wingtip.position.set(sign * 1.78, 0.06, 0.72);

    const ventralFin = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.3, 0.55),
      primaryDark
    );
    ventralFin.position.set(sign * 1.35, -0.12, 0.85);
    ventralFin.rotation.z = sign * -0.4;

    parent.add(wing, wingTop, wingLight, wingtip, ventralFin);
  };

  mkSide(1);
  mkSide(-1);
}

function buildTailFin(parent: THREE.Object3D, skin: ShipSkin): void {
  const primaryMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.45
  });
  const primaryDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const accentMat = createMatteMaterial({
    color: skin.accent,
    roughness: 0.35,
    emissive: skin.accent,
    emissiveIntensity: 0.15
  });

  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(0.85, 0);
  shape.lineTo(0.55, 0.85);
  shape.lineTo(0.1, 0.85);
  shape.closePath();

  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: 0.06,
    bevelEnabled: true,
    bevelSize: 0.018,
    bevelThickness: 0.018,
    bevelSegments: 2
  });
  geo.translate(-0.42, 0, -0.03);
  geo.rotateY(Math.PI / 2);

  const fin = new THREE.Mesh(geo, primaryMat);
  fin.position.set(0, 0.5, 1.0);

  const finEdge = new THREE.Mesh(
    new THREE.BoxGeometry(0.05, 0.05, 0.55),
    primaryDark
  );
  finEdge.position.set(0, 1.3, 1.12);

  const finTip = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, 0.1, 0.15),
    accentMat
  );
  finTip.position.set(0, 1.33, 0.9);

  parent.add(fin, finEdge, finTip);
}

function buildEngines(parent: THREE.Object3D, skin: ShipSkin): EngineMount[] {
  const housingMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.38
  });
  const housingDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const rimMat = createMatteMaterial({ color: skin.trim, roughness: 0.5 });
  const innerMat = createMatteMaterial({
    color: skin.hullDark,
    roughness: 0.7
  });
  const coreMat = createGlowMaterial(skin.glow, 0.9, true);
  const plumeMat = createGlowMaterial(skin.plasma, 0.38, true);

  const mounts: EngineMount[] = [];

  const make = (x: number): EngineMount => {
    const z = 1.85;
    const y = 0.1;
    const scale = 1.0;

    const outrigger = new THREE.Mesh(
      createTaperedSlab(0.3, 0.38, 0.32, 0.7),
      housingMat
    );
    outrigger.position.set(x, y, z - 0.45);

    const housing = new THREE.Mesh(
      new THREE.CylinderGeometry(0.32 * scale, 0.36 * scale, 0.8, 22),
      housingMat
    );
    housing.rotation.x = Math.PI / 2;
    housing.position.set(x, y, z);

    const ringFront = new THREE.Mesh(
      new THREE.TorusGeometry(0.34 * scale, 0.035, 8, 22),
      housingDark
    );
    ringFront.position.set(x, y, z - 0.32);

    const ringMid = new THREE.Mesh(
      new THREE.TorusGeometry(0.36 * scale, 0.03, 8, 22),
      housingDark
    );
    ringMid.position.set(x, y, z);

    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(0.32 * scale, 0.055, 8, 24),
      rimMat
    );
    rim.position.set(x, y, z + 0.4);

    const throat = new THREE.Mesh(
      new THREE.CylinderGeometry(0.25 * scale, 0.29 * scale, 0.38, 20),
      innerMat
    );
    throat.rotation.x = Math.PI / 2;
    throat.position.set(x, y, z + 0.24);

    const core = new THREE.Mesh(
      new THREE.CircleGeometry(0.22 * scale, 24),
      coreMat
    );
    core.position.set(x, y, z + 0.43);

    const plumeGeo = new THREE.ConeGeometry(
      0.27 * scale,
      1.2 * scale,
      22,
      1,
      true
    );
    const plume = new THREE.Mesh(plumeGeo, plumeMat);
    plume.rotation.x = Math.PI / 2;
    plume.position.set(x, y, z + 0.98 * scale);

    parent.add(outrigger, housing, ringFront, ringMid, rim, throat, core, plume);

    return {
      local: new THREE.Vector3(x, y, z + 0.46),
      plume,
      core
    };
  };

  mounts.push(make(-0.55));
  mounts.push(make(0.55));

  return mounts;
}

function buildStrobes(
  parent: THREE.Object3D,
  skin: ShipSkin,
  strobes: THREE.Mesh[]
): void {
  const strobeMat = createGlowMaterial(skin.strobe, 1, true);
  const geo = new THREE.SphereGeometry(0.06, 10, 8);

  const left = new THREE.Mesh(geo, strobeMat);
  left.position.set(-1.78, 0.15, 0.72);
  const right = new THREE.Mesh(geo, strobeMat);
  right.position.set(1.78, 0.15, 0.72);

  const top = new THREE.Mesh(
    geo,
    createGlowMaterial(skin.accent, 1, true)
  );
  top.position.set(0, 1.4, 0.85);

  parent.add(left, right, top);
  strobes.push(left, right, top);
}
